import { useEffect, useRef, useState } from "react";
import {
  Button,
  FormField,
  Input,
  Modal,
  Textarea,
  toast,
} from "@playbookai/ui";
import { useSetKpiTarget } from "../hooks/useSetKpiTarget.js";
import type { KpiTargetSnapshot, SetKpiTargetInput } from "../api/types.js";
import { errorMessage } from "./error-message.js";
import { kpiTargetFigureError } from "./kpi-observation-value.js";

export interface SetTargetModalProps {
  readonly kpiId: string;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly target: KpiTargetSnapshot | null;
}

/**
 * Set / revise the KPI-owned Target (`POST /v1/kpis/:id/targets`). The server
 * closes the current open row and opens a new one; the note records why.
 */
export function SetTargetModal({
  kpiId,
  open,
  onClose,
  target,
}: SetTargetModalProps): JSX.Element {
  const valueRef = useRef<HTMLInputElement>(null);
  const setTarget = useSetKpiTarget();
  const [targetValue, setTargetValue] = useState("");
  const [touched, setTouched] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setTargetValue(target?.targetValue ?? "");
    setNote("");
    setTouched(false);
  }, [open, target]);

  const trimmed = targetValue.trim();
  // Shown as soon as it is wrong, not only after blur: a figure that is not a
  // number is refused in the same words as the TTI page's monthly box.
  const notNumber = kpiTargetFigureError(trimmed, target?.targetValue);
  const valueError =
    touched && !trimmed ? "A target value is required." : notNumber;
  const canSubmit =
    Boolean(trimmed) && notNumber === undefined && !setTarget.isPending;

  const reset = () => {
    setTargetValue("");
    setTouched(false);
    setNote("");
  };

  const handleClose = () => {
    if (setTarget.isPending) return;
    reset();
    onClose();
  };

  const handleSubmit = () => {
    setTouched(true);
    if (!trimmed || notNumber) return;
    const input: {
      -readonly [K in keyof SetKpiTargetInput]?: SetKpiTargetInput[K];
    } = {
      // Preserve the legacy exemption on note-only and whitespace-only edits.
      targetValue:
        trimmed === (target?.targetValue ?? "").trim()
          ? (target?.targetValue ?? trimmed)
          : trimmed,
    };
    // Keep revisions scoped to the currently displayed TTI. The backend can
    // support more than one TTI per KPI even though this UI currently shows one.
    if (target) input.ttiId = target.ttiId;
    if (note.trim()) input.note = note.trim();
    setTarget.mutate(
      { kpiId, input: input as SetKpiTargetInput },
      {
        onSuccess: () => {
          toast("Target updated", { tone: "healthy" });
          reset();
          onClose();
        },
        onError: (err) =>
          toast(errorMessage(err, "Could not set the target."), {
            tone: "critical",
          }),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="sm"
      initialFocusRef={valueRef}
      title={target ? "Edit target" : "Set target"}
      description={
        target
          ? "Revising a target closes the current period and opens a new one."
          : "Set the value this KPI is aiming to reach."
      }
      footer={
        <>
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={setTarget.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={setTarget.isPending}
          >
            Save target
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField label="Target value" required error={valueError}>
          <Input
            ref={valueRef}
            value={targetValue}
            onChange={(event) => setTargetValue(event.target.value)}
            onBlur={() => setTouched(true)}
            maxLength={255}
            placeholder="e.g. 120"
          />
        </FormField>
        <FormField label="Change note">
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            maxLength={1000}
            showCount
          />
        </FormField>
      </div>
    </Modal>
  );
}
